// BUILD-046 section D (Owner direction of 2026-10-21), redesigned by the Owner direction of 2026-10-23: the Presets control that fills the reserved room in the toolbar row (between Sort and Refresh).
//
// One button, "Presets", with the name of the applied preset and a "Modified" mark when the view has moved off it. Its panel is a SELECT and EDIT flow, not a form:
//   - the list: the person's presets for THIS tab in the order the person chose, each as [check if applied] name [star if default] and a "..." button with that preset's own menu
//     (Edit preset, Set as default / Remove default, Move to top, Move up, Move down, Delete); a click on the name applies it;
//   - "Save current view as new..." is a separate action that asks for a name and keeps the filters and sort the page shows now;
//   - Edit preset opens a focused panel (the list and the new-preset action are hidden): the name, the saved filters and up to three sort rows, with Cancel and Save changes. It works on a DRAFT, so
//     the table behind it does not move until Save changes; Save changes updates the SAME preset (same id, same place, same default mark) and, when that preset is the one applied, shows it at once.
// A manual change in the page never changes a preset: only Update (shown beside "Modified") and Save changes do. The order of the list is the person's own and has nothing to do with how the tasks are
// sorted; Move never sets the default and Set as default never moves anything. Density and the display settings are not part of a preset. No preset is company-wide.
//
// The row menu is drawn inline under its row (not in a floating layer), so the panel's scrolling can never clip it and a click in it is a click inside the popover.
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { PopoverButton } from "./DropdownMenu";
import { useNameTip } from "./PersonName";
import { MAX_PRESET_NAME, RULE_KEY_LABELS, normalizeCriteria, ruleDirectionLabel } from "./presetsModel";
import type { Preset, PresetTab, RuleKey } from "./presetsModel";
import { RULE_DIRECTIONS, SAVED_MODE, STATUS_CHOICES, criteriaFromDraft, draftFromPreset, modeAsRule, savedModeLabel, setSortRow } from "./presetEditModel";
import type { EditDraft } from "./presetEditModel";
import { JobFilterPicker } from "./JobFilterPicker";
import { AssigneeFilterPicker } from "./AssigneeFilterPicker";
import { JOB_FILTER_LABEL_BESIDE_CUSTOMER, jobFilterSummary } from "./jobFilterModel";
import type { JobChoiceOption } from "./jobFilterModel";
import { CustomerFilterPicker } from "./CustomerFilterPicker";
import { customerFilterSummary } from "./customerFilterModel";
import type { CustomerChoiceOption } from "./customerFilterModel";
import type { AssigneeChoice } from "./assigneeFilterModel";
import type { PresetsController } from "./usePresets";
import { PRESETS_STORAGE_NOTE } from "./presetsStore";
import type { OrderDirection } from "./taskOrdering";

/** The choices the page already offers for its filters, so the Edit panel says them in the same words. */
export interface PresetChoices {
  quick: readonly { value: string; label: string }[];
  open: readonly { value: string; label: string }[];
  /** Pass F1 (2026-10-25): the Status choices of Assigned by Me and Shared Tasks (the other tabs use `quick`/`open` above). */
  statuses?: readonly { value: string; label: string }[];
  /** The departments on the loaded rows (the saved department is added when it is not among them). */
  departments: readonly string[];
  /** The jobs on the rows this tab has loaded, and the sentence that says so: what the Customer / Job picker of Edit preset searches (the same options as the Filter panel's picker; nothing is fetched). */
  jobs?: { options: readonly JobChoiceOption[]; scopeText: string };
  /** BUILD-046 Customer association: the customers on the rows this tab has loaded, and the sentence that says so. Present only while customers are in play: the Customer picker of Edit preset is drawn only then. */
  customers?: { options: readonly CustomerChoiceOption[]; scopeText: string };
  /** The people on the rows this tab has loaded, and the sentence that says so: what the Assigned to picker of Edit preset searches. */
  people?: { options: readonly AssigneeChoice[]; scopeText: string };
}

export interface PresetsMenuProps {
  controller: PresetsController;
  tab: PresetTab;
  /** The keys a sort row may order by on this tab (the task columns, or the Assigned by Me columns). */
  ruleKeys: readonly RuleKey[];
  tabLabel: string;
  choices: PresetChoices;
  /** What the sort covers when the list is read page by page (only the rows loaded so far); null when every row is loaded. */
  scopeNote?: string | null;
  /** A line about values the list does not carry (Assigned by Me): placed last, never guessed. */
  rulesNote?: string | null;
}

const NO_JOBS: readonly JobChoiceOption[] = Object.freeze([]);
const NO_CUSTOMERS: readonly CustomerChoiceOption[] = Object.freeze([]);
const NO_PEOPLE: readonly AssigneeChoice[] = Object.freeze([]);
const SMALL_BUTTON = "rounded px-1.5 py-0.5 text-xs font-medium text-[#1f6a99] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] disabled:opacity-50";
const FIELD = "w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE]";
const SELECT = "w-full min-w-0 rounded border border-gray-300 bg-white px-1.5 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE] disabled:opacity-50";
const PRIMARY = "rounded bg-[#1f6fa5] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#195d8a] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#2a7fb8] disabled:opacity-50";
const SECONDARY = "rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] disabled:opacity-50";

/** A small "i" that explains something in one sentence on hover AND on keyboard focus (the tooltip the names use). */
function InfoTip({ label, text }: { label: string; text: string }) {
  const tip = useNameTip(text);
  return (
    <>
      <button
        type="button"
        ref={tip.setAnchor}
        aria-label={label}
        title={tip.open ? undefined : text}
        aria-describedby={tip.open ? tip.tipId : undefined}
        data-testid="presets-info"
        {...tip.handlers}
        className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 text-[11px] font-semibold leading-none text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
      >
        i
      </button>
      {tip.overlay}
    </>
  );
}

export function PresetsMenu(props: PresetsMenuProps) {
  const { controller, tabLabel } = props;
  const label = controller.appliedName === null ? "Presets" : "Presets: " + controller.appliedName + (controller.modified ? " (modified)" : "");
  const text = controller.appliedName === null ? "Presets" : controller.appliedName;
  return (
    <PopoverButton
      testId="toolbar-presets"
      buttonText={
        <span className="inline-flex items-center gap-1.5">
          <span className="max-w-[9rem] truncate">{text}</span>
          {controller.modified && <span data-testid="presets-modified" className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">Modified</span>}
        </span>
      }
      ariaLabel={label}
      panelLabel={"Presets for " + tabLabel}
      panelClassName="w-[24rem] max-w-[calc(100vw-1rem)] max-h-[80vh] overflow-y-auto"
      align="left"
      clampToViewport
    >
      <PresetsPanel {...props} />
    </PopoverButton>
  );
}

/** The panel's content. It exists only while the panel is open, so closing the panel also ends an edit that was not saved. */
function PresetsPanel(props: PresetsMenuProps) {
  const { controller } = props;
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = editingId === null ? null : controller.presets.find((preset) => preset.id === editingId) ?? null;
  return (
    <div className="space-y-3" data-testid="presets-panel">
      {editing !== null ? (
        <EditPreset {...props} preset={editing} onDone={() => setEditingId(null)} />
      ) : (
        <PresetList {...props} onEdit={(id) => { controller.dismissMessage(); setEditingId(id); }} />
      )}
    </div>
  );
}

// ───────────────────────── the list ─────────────────────────

interface RowAction {
  key: string;
  label: string;
  disabled?: boolean;
  danger?: boolean;
  /** Focus goes back to the row's "..." button after the action (not for an action that opens something else). */
  refocus: boolean;
  run: () => void;
}

/** One preset's menu, drawn inline under its row. Arrow keys, Home and End move between the actions; Escape closes it (and only it); choosing an action closes it. */
function RowActions({ name, actions, onClose }: { name: string; actions: readonly RowAction[]; onClose: (returnFocus: boolean) => void }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    refs.current.find((element) => element !== null && !element.disabled)?.focus();
  }, []);
  const enabled = (): HTMLButtonElement[] => refs.current.filter((element): element is HTMLButtonElement => element !== null && !element.disabled);
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const items = enabled();
    const at = items.findIndex((element) => element === document.activeElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose(true);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      items[at < 0 ? 0 : (at + 1) % items.length]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[at < 0 ? items.length - 1 : (at - 1 + items.length) % items.length]?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      items[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      items[items.length - 1]?.focus();
    } else if (event.key === "Tab") {
      onClose(false);
    }
  }
  return (
    <div role="menu" aria-label={"Actions for " + name} data-testid="preset-menu" onKeyDown={onKeyDown} className="mb-1 ml-5 mt-0.5 rounded border border-gray-200 bg-white py-1 shadow-sm">
      {actions.map((action, index) => (
        <button
          key={action.key}
          ref={(element) => {
            refs.current[index] = element;
          }}
          type="button"
          role="menuitem"
          disabled={action.disabled === true}
          data-testid={"preset-action-" + action.key}
          onClick={() => {
            onClose(action.refocus);
            action.run();
          }}
          className={"block w-full px-3 py-1.5 text-left text-sm focus:outline-none focus-visible:bg-sky-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4AABDE] disabled:opacity-40 " + (action.danger === true ? "text-red-700 hover:bg-red-50" : "text-gray-800 hover:bg-gray-50")}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

function PresetList({ controller, tabLabel, onEdit }: PresetsMenuProps & { onEdit: (id: string) => void }) {
  const baseId = useId();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const menuButtons = useRef<Record<string, HTMLButtonElement | null>>({});
  const last = controller.presets.length - 1;

  function actionsOf(preset: Preset, index: number): RowAction[] {
    const isDefault = controller.defaultId === preset.id;
    return [
      { key: "edit", label: "Edit preset", refocus: false, run: () => onEdit(preset.id) },
      { key: "default", label: isDefault ? "Remove default" : "Set as default", refocus: true, run: () => controller.makeDefault(isDefault ? null : preset.id) },
      { key: "top", label: "Move to top", disabled: index === 0, refocus: true, run: () => controller.movePresetTo(preset.id, "top") },
      { key: "up", label: "Move up", disabled: index === 0, refocus: true, run: () => controller.movePresetTo(preset.id, "up") },
      { key: "down", label: "Move down", disabled: index === last, refocus: true, run: () => controller.movePresetTo(preset.id, "down") },
      { key: "delete", label: "Delete", danger: true, refocus: false, run: () => setConfirmId(preset.id) },
    ];
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Presets</h4>
        <InfoTip label="About presets" text={PRESETS_STORAGE_NOTE} />
      </div>

      {controller.appliedName !== null && controller.modified && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900" data-testid="presets-applied">
          <span className="font-semibold">Modified</span>
          <button type="button" className={SMALL_BUTTON} data-testid="presets-update" aria-label={"Update " + controller.appliedName + " with this view"} onClick={controller.updateApplied}>Update</button>
          <button type="button" className={SMALL_BUTTON} data-testid="presets-reset" aria-label={"Back to " + controller.appliedName} onClick={controller.resetToApplied}>Reset</button>
        </div>
      )}

      {controller.presets.length === 0 ? (
        <p className="text-xs text-gray-600" data-testid="presets-empty">No presets yet.</p>
      ) : (
        <ul className="space-y-0.5" data-testid="presets-list">
          {controller.presets.map((preset, index) => {
            const active = controller.appliedId === preset.id;
            const isDefault = controller.defaultId === preset.id;
            return (
              <li key={preset.id} data-testid={"preset-" + preset.id}>
                {confirmId === preset.id ? (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-gray-900">
                    <span className="min-w-0 flex-1 break-words">Delete {preset.name}?</span>
                    <button type="button" autoFocus className={SMALL_BUTTON + " text-red-700"} data-testid="preset-delete-yes" onClick={() => { controller.deleteById(preset.id); setConfirmId(null); }}>Delete</button>
                    <button type="button" className={SMALL_BUTTON} onClick={() => setConfirmId(null)}>Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => controller.applyPreset(preset.id)}
                      aria-current={active ? "true" : undefined}
                      data-testid="preset-apply"
                      title={preset.name}
                      className={"flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1 text-left text-sm text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] " + (active ? "font-semibold" : "font-medium")}
                    >
                      <span aria-hidden="true" className="w-4 flex-shrink-0 text-[#2a7fb8]" data-testid={active ? "preset-applied-mark" : undefined}>{active ? "\u2713" : ""}</span>
                      <span className="min-w-0 flex-1 truncate">{preset.name}</span>
                      {isDefault && <span aria-hidden="true" title="Default" data-testid="preset-default-mark" className="flex-shrink-0 text-amber-500">{"\u2605"}</span>}
                      <span className="sr-only">{(active ? " (applied)" : "") + (isDefault ? " (default)" : "")}</span>
                    </button>
                    <button
                      type="button"
                      ref={(element) => {
                        menuButtons.current[preset.id] = element;
                      }}
                      aria-haspopup="menu"
                      aria-expanded={menuFor === preset.id}
                      aria-label={"Actions for " + preset.name}
                      title="Preset actions"
                      data-testid="preset-menu-button"
                      onClick={() => {
                        // The last confirmation ("Saved the preset ...") has done its job once the person moves on to another action.
                        controller.dismissMessage();
                        setMenuFor((current) => (current === preset.id ? null : preset.id));
                      }}
                      className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded border border-transparent text-gray-700 hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
                    >
                      <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
                        <circle cx="4.5" cy="10" r="1.6" />
                        <circle cx="10" cy="10" r="1.6" />
                        <circle cx="15.5" cy="10" r="1.6" />
                      </svg>
                    </button>
                  </div>
                )}
                {menuFor === preset.id && confirmId !== preset.id && (
                  <RowActions
                    name={preset.name}
                    actions={actionsOf(preset, index)}
                    onClose={(returnFocus) => {
                      setMenuFor(null);
                      if (returnFocus) menuButtons.current[preset.id]?.focus();
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="border-t border-gray-200 pt-2">
        {creating ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (controller.savePreset(newName)) {
                setNewName("");
                setCreating(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setCreating(false);
                setNewName("");
              }
            }}
            className="space-y-1"
          >
            <label htmlFor={baseId + "-new"} className="block text-xs font-medium text-gray-700">Name for the new preset</label>
            <div className="flex items-center gap-2">
              <input id={baseId + "-new"} autoFocus value={newName} maxLength={MAX_PRESET_NAME + 10} placeholder="For example Monday review" onChange={(event) => setNewName(event.target.value)} className={FIELD} />
              <button type="submit" data-testid="presets-save" className={PRIMARY}>Save</button>
              <button type="button" className={SECONDARY} onClick={() => { setCreating(false); setNewName(""); }}>Cancel</button>
            </div>
          </form>
        ) : (
          <button type="button" data-testid="presets-new" disabled={!controller.canSave} onClick={() => { controller.dismissMessage(); setCreating(true); }} className={SMALL_BUTTON}>Save current view as new{"\u2026"}</button>
        )}
      </div>

      {controller.message !== "" && (
        <p role="status" aria-live="polite" data-testid="presets-message" className="flex items-start justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-xs text-gray-800">
          <span>{controller.message}</span>
          <button type="button" onClick={controller.dismissMessage} aria-label="Dismiss message" className="font-bold">&times;</button>
        </p>
      )}
      <span className="sr-only">Presets for {tabLabel}</span>
    </>
  );
}

// ───────────────────────── Edit preset ─────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-xs text-gray-700">
      <span className="mb-0.5 block font-medium">{label}</span>
      {children}
    </label>
  );
}

function EditPreset({ controller, tab, ruleKeys, choices, scopeNote = null, rulesNote = null, preset, onDone }: PresetsMenuProps & { preset: Preset; onDone: () => void }) {
  const base = normalizeCriteria(tab, preset.criteria);
  const [draft, setDraft] = useState<EditDraft>(() => draftFromPreset(tab, preset));
  const headingId = useId();
  const change = (patch: Partial<EditDraft>): void => setDraft((current) => ({ ...current, ...patch }));
  const setRow = (index: number, patch: { key?: RuleKey | "" | typeof SAVED_MODE; direction?: OrderDirection }): void =>
    setDraft((current) => ({ ...current, sort: setSortRow(current.sort, index, patch), sortTouched: true }));
  // A saved single mode that no rule can stand for (Oldest, Newest, Project, Needs attention) is offered as "As saved: ..." on the first row, so the person can keep it or choose something else.
  const savedOnly = base.rules.length === 0 && base.mode !== "recommended" && modeAsRule(tab, base.mode) === null;
  const departments = draft.department !== "" && !choices.departments.includes(draft.department) ? [draft.department, ...choices.departments] : choices.departments;
  const showStatus = tab === "my-work";
  const showOpen = tab === "team-work";
  const showTabStatus = tab === "issued" || tab === "group-shared";
  const showJob = tab !== "issued";
  // BUILD-046 Customer association: the Customer picker is drawn (before the Job picker, which then says "Job") only when the page says customers are in play (it passes `choices.customers`).
  const showCustomer = tab !== "issued" && choices.customers !== undefined;
  const customerOptions = choices.customers?.options ?? NO_CUSTOMERS;
  const customerScope = choices.customers?.scopeText ?? "";
  const showAssignee = tab === "team-work" || tab === "issued";
  const jobOptions = choices.jobs?.options ?? NO_JOBS;
  const jobScope = choices.jobs?.scopeText ?? "";
  const peopleOptions = choices.people?.options ?? NO_PEOPLE;
  const peopleScope = choices.people?.scopeText ?? "";
  const personWords = draft.assignee.kind === "person" ? draft.assignee.label || draft.assignee.fullName || "Selected person" : "Anyone";
  const sortInfo = [rulesNote, scopeNote].filter((part): part is string => typeof part === "string" && part !== "").join(" ");
  const cancel = (): void => {
    controller.dismissMessage();
    onDone();
  };
  return (
    <form
      data-testid="preset-edit"
      aria-labelledby={headingId}
      onSubmit={(event) => {
        event.preventDefault();
        if (controller.editPresetById(preset.id, draft.name, criteriaFromDraft(tab, preset.criteria, draft))) onDone();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          cancel();
        } else if (event.key === "Enter" && event.target instanceof HTMLInputElement && event.target.getAttribute("role") === "combobox") {
          // Enter in a picker's search box chooses a match (the picker handles that) and never saves the preset.
          event.preventDefault();
        }
      }}
      className="space-y-3"
    >
      <h3 id={headingId} className="break-words text-sm font-semibold text-gray-900">Edit preset: {preset.name}</h3>

      <Field label="Name">
        <input autoFocus data-testid="preset-edit-name" value={draft.name} maxLength={MAX_PRESET_NAME + 10} onChange={(event) => change({ name: event.target.value })} className={FIELD} />
      </Field>

      <fieldset className="space-y-2 rounded border border-gray-200 p-2">
        <legend className="px-1 text-xs font-semibold text-gray-700">Saved filters</legend>
        {showStatus && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Status">
              <select data-testid="preset-edit-status" value={draft.status} onChange={(event) => change({ status: event.target.value })} className={SELECT}>
                {STATUS_CHOICES.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
              </select>
            </Field>
            <Field label="Quick filter">
              <select data-testid="preset-edit-quick" value={draft.quick} onChange={(event) => change({ quick: event.target.value })} className={SELECT}>
                {choices.quick.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
              </select>
            </Field>
          </div>
        )}
        {showStatus && draft.status === "active" && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-700" data-testid="preset-edit-active-only">
            <span>This preset shows Active tasks only, so a task that becomes In progress, Waiting or Blocked leaves it.</span>
            <button type="button" className={SMALL_BUTTON} data-testid="preset-edit-use-open" onClick={() => change({ status: "open" })}>Show all open tasks</button>
          </p>
        )}
        {showTabStatus && (
          <Field label="Status">
            <select data-testid="preset-edit-status" value={draft.status} onChange={(event) => change({ status: event.target.value })} className={SELECT}>
              {(choices.statuses ?? []).map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
            </select>
          </Field>
        )}
        {showOpen && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Show">
              <select data-testid="preset-edit-open" value={draft.open} onChange={(event) => change({ open: event.target.value })} className={SELECT}>
                {choices.open.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
              </select>
            </Field>
            <Field label="Department">
              <select data-testid="preset-edit-department" value={draft.department} onChange={(event) => change({ department: event.target.value })} className={SELECT}>
                <option value="">All departments</option>
                {departments.map((department) => <option key={department} value={department}>{department}</option>)}
              </select>
            </Field>
          </div>
        )}
        {showCustomer && (
          <div className="space-y-1 rounded border border-gray-200 bg-gray-50 p-2" data-testid="preset-edit-customer-block">
            <CustomerFilterPicker idPrefix="preset-edit" filter={draft.customer} options={customerOptions} scopeText={customerScope} onChange={(next) => change({ customer: next })} />
            <p className="text-xs text-gray-700">
              Selected: <span className="font-medium" data-testid="preset-edit-customer">{customerFilterSummary(draft.customer)}</span>
            </p>
          </div>
        )}
        {showJob && (
          <div className="space-y-1 rounded border border-gray-200 bg-gray-50 p-2" data-testid="preset-edit-job-block">
            <JobFilterPicker idPrefix="preset-edit" filter={draft.job} options={jobOptions} scopeText={jobScope} onChange={(next) => change({ job: next })} label={showCustomer ? JOB_FILTER_LABEL_BESIDE_CUSTOMER : undefined} />
            <p className="text-xs text-gray-700">
              Selected: <span className="font-medium" data-testid="preset-edit-job">{jobFilterSummary(draft.job)}</span>
            </p>
          </div>
        )}
        {showAssignee && (
          <div className="space-y-1 rounded border border-gray-200 bg-gray-50 p-2" data-testid="preset-edit-assignee-block">
            <AssigneeFilterPicker idPrefix="preset-edit" filter={draft.assignee} options={peopleOptions} scopeText={peopleScope} onChange={(next) => change({ assignee: next })} />
            <p className="text-xs text-gray-700">
              Selected: <span className="font-medium" data-testid="preset-edit-assignee" title={draft.assignee.kind === "person" ? draft.assignee.fullName || draft.assignee.label : undefined}>{personWords}</span>
            </p>
          </div>
        )}
      </fieldset>

      <fieldset className="space-y-1.5 rounded border border-gray-200 p-2" data-testid="preset-edit-sort">
        <legend className="flex items-center gap-1.5 px-1 text-xs font-semibold text-gray-700">
          <span>Sort</span>
          {sortInfo !== "" && <InfoTip label="About sorting" text={sortInfo} />}
        </legend>
        {draft.sort.map((row, index) => {
          const previousSet = index === 0 || (draft.sort[index - 1].key !== "" && draft.sort[index - 1].key !== SAVED_MODE);
          const usedElsewhere = new Set(draft.sort.filter((_, at) => at !== index).map((other) => other.key));
          const keys = ruleKeys.filter((key) => key === row.key || !usedElsewhere.has(key));
          const hasKey = row.key !== "" && row.key !== SAVED_MODE;
          const word = index === 0 ? "Sort by" : "Then by";
          const suffix = index === 2 ? " (third)" : "";
          return (
            <div key={index} className="grid grid-cols-[3.75rem_minmax(0,1fr)_minmax(0,1fr)] items-center gap-1.5" data-testid={"preset-edit-sort-" + (index + 1)}>
              <span className="text-xs text-gray-700" aria-hidden="true">{word}</span>
              <select aria-label={word + suffix} value={row.key} disabled={!previousSet} onChange={(event) => setRow(index, { key: event.target.value as RuleKey | "" | typeof SAVED_MODE })} className={SELECT}>
                <option value="">None</option>
                {index === 0 && savedOnly && <option value={SAVED_MODE}>As saved: {savedModeLabel(base.mode)}</option>}
                {keys.map((key) => <option key={key} value={key}>{RULE_KEY_LABELS[key]}</option>)}
              </select>
              {hasKey ? (
                <select aria-label={word + " direction" + suffix} value={row.direction} onChange={(event) => setRow(index, { direction: event.target.value as OrderDirection })} className={SELECT}>
                  {RULE_DIRECTIONS[row.key as RuleKey].map((direction) => <option key={direction} value={direction}>{ruleDirectionLabel(row.key as RuleKey, direction)}</option>)}
                </select>
              ) : (
                <span />
              )}
            </div>
          );
        })}
      </fieldset>

      {controller.message !== "" && (
        <p role="alert" data-testid="presets-message" className="rounded bg-red-50 px-2 py-1 text-xs text-red-800">{controller.message}</p>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" data-testid="preset-edit-cancel" className={SECONDARY} onClick={cancel}>Cancel</button>
        <button type="submit" data-testid="preset-edit-save" className={PRIMARY}>Save changes</button>
      </div>
    </form>
  );
}

